import { Alert, Dropdown, Menu, Modal, notification, Table, Tag } from "antd"
import { BrokerRoles, IOpusUser, UserStatus } from "platform-client/types"
import usePlatform from "contexts/platform/use-platform"
import useOpus from "contexts/opus/hooks/use-opus"
import React, { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { RouteComponentProps, withRouter } from "react-router"
import { AxiosError } from "axios"
import { useAuthenticatedUserContext } from "contexts/authorization/authenticated-user-context"
import { MoreOutlined } from "@ant-design/icons"
import { EUserStatus } from "containers/users/services/hooks/use-get-users"
import Column from "antd/lib/table/Column"
import UserModal, { IUpdateUserRequest } from "../AddUserModal"
import ManageOfficesModal from "../ManageOfficesModal"
import { canWorkInSeveralBranches } from "../../utils/multi-branch-roles"
import { handleValidationErrors } from "utils/pocketknife/handle-validation-errors"
import {
  EOpusPermission,
  useOpusPermissions
} from "contexts/authorization/hooks/use-opus-permissions"

const states: Record<UserStatus, { tag: string; color: string }> = {
  Active: { tag: "Active", color: "green" },
  Stub: { tag: "Invited", color: "blue" },
  Disabled: { tag: "Disabled", color: "red" },
  Locked: { tag: "Locked", color: "red" },
  Deleted: { tag: "Deleted", color: "red" }
}

const ListBrokerUsers: React.FC<ListBrokerUsersProps> = (props) => {
  const authUser = useAuthenticatedUserContext()
  const platform = usePlatform()
  const { userClient } = useOpus()
  const queryClient = useQueryClient()

  const [editingUser, setEditingUser] = useState<IOpusUser>()
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(false)
  const [showOfficesModal, setShowOfficesModal] = useState<boolean>(false)

  const { havePermission: canManageUsers } = useOpusPermissions(
    EOpusPermission.UserManagment,
    "maintain"
  )

  const userList = useQuery(
    ["listBrokerUsers", props.companyID],
    () => platform.company.listCompanyUsers(props.companyID),
    {
      enabled: !!props.companyID,
      onError: (error: AxiosError) => {
        notification.error({
          message: "Unable to load broker users."
        })
      }
    }
  )

  const getUser = async (userID: string) => {
    setEditingUser(undefined)
    if (userID) {
      const user = await platform.user.getUser(userID)
      setEditingUser(user)
    }
  }

  const resendInvite = async (userID: string) => {
    try {
      const resent = await userClient.setUserResend(userID)
      if (resent) {
        notification.success({
          message: "Invitation email has been resent."
        })
      }
    } catch (error) {
      handleValidationErrors(error as AxiosError)
    }
  }

  const updateUser = useMutation(
    ({ userId, body }: IUpdateUserRequest) =>
      platform.user.updateUser(userId, body),
    {
      onSuccess: () => {
        setShowEditModal(false)
        queryClient.invalidateQueries(["listBrokerUsers", props.companyID])
      },
      onError: (error: AxiosError) => handleValidationErrors(error)
    }
  )

  const editModal = (
    <UserModal
      isEditing={true}
      visible={showEditModal}
      user={editingUser}
      onCancel={() => setShowEditModal(false)}
      onUpdateUser={updateUser.mutate}
      isLoading={isFetchingUser}
      isSaving={updateUser.isLoading}
      company={editingUser?.company}
      allowedRoles={
        editingUser?.company?.parentCompanyID
          ? [BrokerRoles.AccountManager, BrokerRoles.User]
          : [BrokerRoles.BrokerAdministrator]
      }
    />
  )

  const officesModal = (
    <ManageOfficesModal
      visible={showOfficesModal}
      user={editingUser}
      company={editingUser?.company}
      isLoading={isFetchingUser}
      onClose={() => setShowOfficesModal(false)}
    />
  )

  // the full user is loaded for the modals, eg their roles
  const openModalForUser = async (userID: string, show: () => void) => {
    show()
    setIsFetchingUser(true)
    try {
      await getUser(userID)
    } finally {
      setIsFetchingUser(false)
    }
  }

  const disableModal = (userID: string) =>
    Modal.confirm({
      title: "Are you sure you want to disable this user?",
      content: "You won't be able to undo this action",
      centered: true,
      okText: "Disable",
      okButtonProps: {
        danger: true
      },
      onOk: async () => {
        if (userID) {
          try {
            const userDisabled = await platform.user.disableUser(userID)
            if (userDisabled) {
              queryClient.invalidateQueries([
                "listBrokerUsers",
                props.companyID
              ])
            }
          } catch (error) {
            handleValidationErrors(error as AxiosError)
          }
        }
      }
    })

  return (
    <>
      {userList.data && userList.data.length === 0 && (
        <Alert
          message="No users found for this broker."
          style={{ marginBottom: "15px" }}
        />
      )}
      <Table
        rowKey="id"
        scroll={{ x: "max-content" }}
        loading={userList.isLoading || userList.isFetching}
        dataSource={userList.data}
        pagination={{
          defaultPageSize: 10,
          hideOnSinglePage: true,
          position: ["bottomCenter"],
          size: "small"
        }}
      >
        <Column
          dataIndex="fullName"
          title="Name"
          key="name"
          sorter={(a: IOpusUser, b: IOpusUser) =>
            a.fullName ? a.fullName.localeCompare(b.fullName || "") : 0
          }
        />
        <Column title="Email" dataIndex="username" key="email" />
        <Column
          title="Status"
          dataIndex="status"
          key="status"
          render={(status: UserStatus) => {
            return (
              <Tag color={states[status].color}>
                {states[status].tag.toUpperCase()}
              </Tag>
            )
          }}
        />
        <Column
          title="Last login"
          dataIndex="lastLogin"
          key="lastLogin"
          render={(lastLogin: string) =>
            lastLogin && new Date(lastLogin).toLocaleDateString()
          }
        />
        <Column
          title="Actions"
          render={(record: IOpusUser) => (
            <>
              {canManageUsers &&
                authUser?.user?.email !== record.username &&
                record.status !== EUserStatus.Disabled && (
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item onClick={() => disableModal(record.id)}>
                          Disable
                        </Menu.Item>
                        <Menu.Item
                          onClick={() =>
                            openModalForUser(record.id, () =>
                              setShowEditModal(true)
                            )
                          }
                        >
                          Edit
                        </Menu.Item>
                        {/* only account managers and users can belong to several offices (eg not a Broker Administrator) */}
                        {canWorkInSeveralBranches(record) && (
                          <Menu.Item
                            onClick={() =>
                              openModalForUser(record.id, () =>
                                setShowOfficesModal(true)
                              )
                            }
                          >
                            Manage Offices
                          </Menu.Item>
                        )}
                        {record.status === EUserStatus.Invited && (
                          <Menu.Item onClick={() => resendInvite(record.id)}>
                            Re-send invite
                          </Menu.Item>
                        )}
                      </Menu>
                    }
                  >
                    <MoreOutlined
                      style={{
                        cursor: "pointer",
                        padding: "0 5px",
                        fontSize: 19
                      }}
                    />
                  </Dropdown>
                )}
            </>
          )}
        />
      </Table>
      {editModal}
      {officesModal}
    </>
  )
}

export type ListBrokerUsersProps = RouteComponentProps & {
  companyID: string
}

export default withRouter(ListBrokerUsers)
